import RefreshIcon from '@mui/icons-material/Refresh';
import { FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';

import { ResourceTypeDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useDeleteFile } from '@/api/requests/files/useDeleteFile.util.ts';
import { useGetFiles } from '@/api/requests/files/useGetFiles.util.ts';
import { useUploadFile } from '@/api/requests/files/useUploadFile.util.ts';
import { FileUploadItem } from '@/components/fileUpload/components/FileUploadItem.comp.tsx';
import { FileUpload } from '@/components/fileUpload/FileUpload.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface Props {
  resourceId: string;
  resourceType: ResourceTypeDto;
}

const DOCUMENT: Set<string> = new Set([
  'csv',
  'doc',
  'docx',
  'odp',
  'ods',
  'odt',
  'pdf',
  'ppt',
  'pptx',
  'txt',
  'xls',
  'xlsx',
]);
const IMAGES: Set<string> = new Set(['bmp', 'gif', 'jpeg', 'jpg', 'png', 'webp']);
const SIZE_THRESHOLD_KB = 100;

export const ItemDetailsFilesTab: React.FC<Props> = props => {
  const { resourceId, resourceType } = props;
  const { t } = useTranslationWithPrefix('components.filesTab');
  const queryClient = useQueryClient();
  const [documentsOnly, setDocumentsOnly] = useState(true);
  const [imagesOnly, setImagesOnly] = useState(false);
  const [bigFilesOnly, setBigFilesOnly] = useState(false);
  const [filter, setFilter] = useState('');

  const { files, isUpdating } = useGetFiles({
    resourceId,
    resourceType,
  });
  const { deleteFile } = useDeleteFile();

  const { isUploading, uploadFile } = useUploadFile();

  const handleRefresh = () => {
    void queryClient.refetchQueries({
      queryKey: [QueryKeyEnum.FILES, resourceId, resourceType],
    });
  };

  const handleUploadFile = async (file: File) => {
    await uploadFile({
      file: file,
      resourceId,
      resourceType,
    });
  };

  const filteredFiles = useMemo(() => {
    return files.filter(it => {
      const fileExtension = getFileExtension(it.name);
      const visible = !bigFilesOnly || it.size / 1024 > SIZE_THRESHOLD_KB;
      if (!visible) {
        return false;
      }

      if (!documentsOnly && !imagesOnly && filter.length === 0) {
        return true;
      }

      if (filter.length > 0 && !it.name.toLowerCase().includes(filter.toLowerCase())) {
        return false;
      }

      if (!documentsOnly && !imagesOnly) {
        return true;
      }

      return (
        (documentsOnly && DOCUMENT.has(fileExtension)) || (imagesOnly && IMAGES.has(fileExtension))
      );
    });
  }, [bigFilesOnly, documentsOnly, files.length, imagesOnly, filter]);

  return (
    <Stack sx={{ backgroundColor: 'white', p: 4 }}>
      <FileUpload
        dropFieldSx={{ minHeight: 20 }}
        isUploading={isUploading}
        onUploadData={handleUploadFile}
      />
      <Divider />
      <Stack direction="row" spacing={1}>
        <TextField
          label="Nazwa"
          onChange={e => setFilter(e.target.value)}
          value={filter}
          variant="standard"
        />
        <FormControlLabel
          control={
            <Checkbox checked={documentsOnly} onChange={() => setDocumentsOnly(cur => !cur)} />
          }
          label="Pokaż dokumenty"
        />
        <FormControlLabel
          control={<Checkbox checked={imagesOnly} onChange={() => setImagesOnly(cur => !cur)} />}
          label="Pokaż obrazy"
        />
        <FormControlLabel
          control={
            <Checkbox checked={bigFilesOnly} onChange={() => setBigFilesOnly(cur => !cur)} />
          }
          label="Tylko duże pliki ( > 100 KB)"
        />
      </Stack>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
        <IconButton loading={isUpdating} onClick={handleRefresh}>
          <RefreshIcon sx={{ color: themeColors.accentColor }} />
        </IconButton>
      </Stack>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell width="35%">{t('table.head.name')}</TableCell>
            <TableCell width="20%">{t('table.head.createdBy')}</TableCell>
            <TableCell width="10%">{t('table.head.resourceType')}</TableCell>
            <TableCell width="10%">{t('table.head.createdAt')}</TableCell>
            <TableCell width="10%">{t('table.head.size')}</TableCell>
            <TableCell width="10%">{t('table.head.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredFiles.map(file => (
            <FileUploadItem
              file={file}
              isReady={true}
              key={file.externalFileId}
              onDeleteFile={deleteFile}
            />
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};

const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return '';
  }
  return fileName.substring(lastDotIndex + 1);
};
